import React from 'react';
import MTimePicker from './MTimePicker';
import MInputField, { MInputFieldProps } from './MInputField';
import { parseTimeString, formatTimeString } from './MTimePicker';
import { IconClock } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

/**
 * Props for the MTimeInputField atom component.
 *
 * @property value - (Optional) Controlled time string value (e.g., '14:30' or '02:30 PM').
 * @property onChange - (Optional) Callback when the time changes, receives the formatted time string.
 * @property label - (Optional) Label for the input field.
 * @property showLabel - (Optional) Whether to show the label. Defaults to true.
 * @property placeholder - (Optional) Placeholder text for the input.
 * @property disabled - (Optional) Whether the input is disabled.
 * @property readOnly - (Optional) Whether the input is read-only.
 * @property format - (Optional) 12 for 12-hour (AM/PM), 24 for 24-hour. Defaults to 24.
 * @property variant - (Optional) Input field style variant.
 * @property icon - (Optional) Icon to display in the input field. Defaults to clock icon.
 * @property showIcon - (Optional) Whether to show the icon. Defaults to true.
 * @property use15MinIncrements - (Optional) When true, minutes will be shown in 15-minute increments (0, 15, 30, 45)
 *                               and the default time will be 8:00 AM instead of current time.
 *
 * Usage:
 * - Controlled: <MTimeInputField value={value} onChange={setValue} format={12} />
 * - Uncontrolled: <MTimeInputField format={24} />
 * - With 15-min increments: <MTimeInputField use15MinIncrements />
 */
export interface MTimeInputFieldProps extends Omit<MInputFieldProps, 'type' | 'onClick' | 'readOnly' | 'value' | 'onChange' | 'icon' | 'showIcon'> {
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  showLabel?: boolean;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  format?: 12 | 24;
  variant?: 'Outlined' | 'Underlined' | 'SummaryDisplay';
  size?: 'md' | 'xl';
  className?: string;
  timePickerTitle?: string;
  icon?: React.ReactNode;
  showIcon?: boolean;
  /**
   * When true, minutes will be shown in 15-minute increments (0, 15, 30, 45)
   * and the default time will be 8:00 AM instead of current time
   */
  use15MinIncrements?: boolean;
}

/**
 * MTimeInputField is an atom component for time input, using a read-only input and a modal time picker drawer.
 *
 * - Supports both controlled and uncontrolled usage.
 * - Uses 24-hour format by default; set `format={12}` for 12-hour input.
 * - Prevents manual typing; time is selected via a modal wheel picker.
 * - Calls onChange with a formatted string (e.g., '14:30' or '02:30 PM').
 *
 * Example (controlled):
 * ```tsx
 * <MTimeInputField value={time} onChange={setTime} format={12} />
 * ```
 * Example (uncontrolled):
 * ```tsx
 * <MTimeInputField format={24} />
 * ```
 */
export const MTimeInputField: React.FC<MTimeInputFieldProps> = ({
  value,
  onChange,
  label,
  showLabel = true,
  placeholder = '',
  disabled = false,
  readOnly = false,
  format = 12,
  timePickerTitle = null,
  variant = 'Outlined',
  size = 'md',
  className = '',
  icon = <IconClock size={20} />,
  showIcon = true,
  use15MinIncrements = true,
  ...inputFieldProps
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState(value || '');
  React.useEffect(() => {
    if (value !== undefined) setInternalValue(value);
  }, [value]);

  const handleInputClick = () => {
    if (!disabled && !readOnly) {
      setIsOpen(true);
    }
  };
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!disabled && !readOnly) {
      setIsOpen(true);
      e.preventDefault();
    }
  };
  const handleDone = (date: Date) => {
    const formatted = formatTimeString(date, format);
    setInternalValue(formatted);
    if (onChange) onChange(formatted);
    setIsOpen(false);
  };

  const getInitialTimeValue = (inputValue: string, format: 12 | 24) => {

    const parsedTime = parseTimeString(inputValue, format);
    if (parsedTime) return parsedTime;

    const defaultTime = new Date();
    defaultTime.setHours(9, 0, 0, 0); // Set to 8:00:00.000 AM
    return defaultTime;
  };

  const inputValue = value !== undefined ? value : internalValue;

  return (
    <>
      <MInputField
        {...inputFieldProps}
        label={label}
        showLabel={showLabel}
        placeholder={placeholder || t('common.notAnswered')}
        value={inputValue}
        readOnly
        disabled={disabled}
        variant={variant}
        size={size}
        className={className}
        onClick={handleInputClick}
        onFocus={handleInputFocus}
        // Do not allow manual typing, so no onChange handler
        type="text"
        icon={icon}
        showIcon={showIcon}
        onIconClick={handleInputClick}
      />
      <MTimePicker
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onDone={handleDone}
        initialValue={getInitialTimeValue(inputValue, format)}
        title={timePickerTitle || t('common.selectTime')}
        format={format}
        minuteStep={use15MinIncrements ? 15 : 1}
      />
    </>
  );
};

export default MTimeInputField;
